// Svelte 5 no longer needs svelte/store imports for basic reactivity

/** Default column visibility for the events table view */
export const DEFAULT_TABLE_COLUMN_VISIBILITY = {
    number: true,
    errors: true,
    start: true,
    end: true,
    duration: true,
    cps: true,
    alignment: true,
    xPos: true,
    yPos: true,
    style: true,
    text: true,
    // Hidden by default (letter tracks A-D; also used by multiview)
    track: false,
    // Hidden by default
    original: false,
    speakers: false,
    forced: false,
    approved: false,
    notes: false,
    wpm: false,
    reply: false,
    notesCheck: false,
    replyCheck: false,
    voice: false,
    recipients: false,
    tags: false,
};

/** Default column order (`number` is always pinned first) */
export const DEFAULT_TABLE_COLUMN_ORDER = [
    "number",
    "errors",
    "track",
    "start",
    "end",
    "duration",
    "cps",
    "alignment",
    "xPos",
    "yPos",
    "style",
    "text",
    "original",
    "speakers",
    "forced",
    "approved",
    "notes",
    "wpm",
    "reply",
    "notesCheck",
    "replyCheck",
    "voice",
    "recipients",
    "tags",
];

/**
 * Merge a saved column order with the current default list.
 * Unknown ids are dropped; new ids are appended in default order.
 * `number` is always forced to the front.
 * @param {string[] | undefined | null} saved
 * @returns {string[]}
 */
export function mergeTableColumnOrder(saved) {
    const known = new Set(DEFAULT_TABLE_COLUMN_ORDER);
    const fromSaved = Array.isArray(saved)
        ? saved.filter((id) => id !== "number" && known.has(id))
        : [];
    const seen = new Set(fromSaved);
    for (const id of DEFAULT_TABLE_COLUMN_ORDER) {
        if (id === "number" || seen.has(id)) continue;
        fromSaved.push(id);
    }
    return ["number", ...fromSaved];
}

export const DEFAULT_TABLE_TEXT_EDITOR_WIDTH = 480;
export const DEFAULT_TABLE_TEXT_EDITOR_HEIGHT = 160;
export const MIN_TABLE_TEXT_EDITOR_WIDTH = 280;
export const MAX_TABLE_TEXT_EDITOR_WIDTH = 1200;
export const MIN_TABLE_TEXT_EDITOR_HEIGHT = 120;
export const MAX_TABLE_TEXT_EDITOR_HEIGHT = 600;

/**
 * @param {unknown} value
 * @param {number} fallback
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
function clampEditorSize(value, fallback, min, max) {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
        return fallback;
    }
    return Math.min(max, Math.max(min, Math.round(parsed)));
}

/**
 * @param {unknown} value
 * @returns {number}
 */
export function clampTableTextEditorWidth(value) {
    return clampEditorSize(
        value,
        DEFAULT_TABLE_TEXT_EDITOR_WIDTH,
        MIN_TABLE_TEXT_EDITOR_WIDTH,
        MAX_TABLE_TEXT_EDITOR_WIDTH,
    );
}

/**
 * @param {unknown} value
 * @returns {number}
 */
export function clampTableTextEditorHeight(value) {
    return clampEditorSize(
        value,
        DEFAULT_TABLE_TEXT_EDITOR_HEIGHT,
        MIN_TABLE_TEXT_EDITOR_HEIGHT,
        MAX_TABLE_TEXT_EDITOR_HEIGHT,
    );
}

function loadEditorSettingsFromLocalStorage() {    let editorSettings = JSON.parse(localStorage.getItem("cc-editor-defaults")) || {
        insertStartOnInsert: true,
        insertEndOnInsert: false,
        selectNextEvent: false,
        autoChain: false,
        autoLineBreak: false,
        autoInsertEvent: false,
        pauseWhileTyping: false,
        pauseWhileTypeDuration: 500,
        autoTrimEvent: false,
        snapToShotChanges: true,
        snapToRegions: true,
        minFrameGap: 0,
        autoFormatText: false,
        updateTimeOnEdit: true,
        collapsePositionStyleColumn: false,
        drawEvents: true,
        timelineFontSize: 12,
        timelineFontColor: "#ffffff",
        timelineOverlapProtection: true,
        showMarkerComment: true,
        showFilmstrip: false,
        filmstripFrameCount: 12,
        scrollToZoom: true,
        speakerAssignmentBehavior: "never"
    };

    editorSettings.editing = false; //keep track of editing state
    editorSettings.speakerAssignmentBehavior = editorSettings.speakerAssignmentBehavior || "never"; // Ensure default value
    editorSettings.showFilmstrip = editorSettings.showFilmstrip ?? false; // Konva timeline: desktop film strip row
    editorSettings.scrollToZoom = editorSettings.scrollToZoom ?? true; // Konva timeline: wheel/trackpad scroll zooms
    editorSettings.collapsePositionStyleColumn = editorSettings.collapsePositionStyleColumn ?? false;
    const parsedFrameCount = Number(editorSettings.filmstripFrameCount);
    editorSettings.filmstripFrameCount = Number.isFinite(parsedFrameCount)
        ? Math.min(30, Math.max(1, Math.round(parsedFrameCount)))
        : 12; // frames before/after the selected event start (1-30)
    editorSettings.snapToRegions = editorSettings.snapToRegions ?? true; // Konva timeline: snap to event edges
    editorSettings.eventsViewMode =
        editorSettings.eventsViewMode === "table" ? "table" : "list";
    editorSettings.tableColumnVisibility = {
        ...DEFAULT_TABLE_COLUMN_VISIBILITY,
        ...(editorSettings.tableColumnVisibility || {}),
    };
    editorSettings.tableColumnOrder = mergeTableColumnOrder(
        editorSettings.tableColumnOrder,
    );
    editorSettings.tableTextEditorWidth = clampTableTextEditorWidth(
        editorSettings.tableTextEditorWidth,
    );
    editorSettings.tableTextEditorHeight = clampTableTextEditorHeight(
        editorSettings.tableTextEditorHeight,
    );
    return editorSettings;
}

// Default filter state (session-only, not persisted to localStorage)
function getDefaultFilterState() {
    return {
        panelVisible: false, // Controls UI visibility of filter panel
        text: "",
        hasError: false,
        hasMissingTimecode: false,
        hasForced: false, // Filter for forced events
        hasNonForced: false, // Filter for non-forced events (forced === false)
        hasFailedApproval: false, // Filter for events with failed approval (approved === false)
        hasApproved: false, // Filter for approved events (approved === true)
        hasNeedsReview: false, // Filter for events needing review (approved === undefined)
        hasIncompleteComment: false, // Filter for events with unchecked notes/replies
        hasRenderRequired: false, // Filter for AD/dubbing events that need TTS or re-record
        lineCounts: [], // Array of line counts to match (4 means "4 or more")
        speakers: [], // Array of speaker IDs/names
        tags: [], // Array of tag names
        tracks: [] // Array of track values: "none" | "A" | "B" | "C" | "D" | numeric (multiview)
    };
}

// Create reactive state for editor settings
export const editorState = $state(loadEditorSettingsFromLocalStorage());

// Session-only filter state (separate from persisted settings)
export const filterState = $state(getDefaultFilterState());

// Function to reset all filters to default (also closes panel)
export function resetFilters() {
    const defaults = getDefaultFilterState();
    Object.keys(defaults).forEach(key => {
        filterState[key] = defaults[key];
    });
}

// Function to check if any filters are active (has filter values set)
export function hasActiveFilters() {
    return (
        filterState.text.trim() !== "" ||
        filterState.hasError ||
        filterState.hasMissingTimecode ||
        filterState.hasForced ||
        filterState.hasNonForced ||
        filterState.hasFailedApproval ||
        filterState.hasApproved ||
        filterState.hasNeedsReview ||
        filterState.hasIncompleteComment ||
        filterState.hasRenderRequired ||
        filterState.lineCounts.length > 0 ||
        filterState.speakers.length > 0 ||
        filterState.tags.length > 0 ||
        filterState.tracks.length > 0
    );
}

// Function to save editor settings to localStorage
export function saveEditorSettings() {
    localStorage.setItem("cc-editor-defaults", JSON.stringify(editorState));
}